import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Archive, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { archivePayroll, listPayrollArchive } from "../../api/payroll";
import { fileUrlAuth } from "../../api/fileUrl";

// أرشيف الرواتب: ملف Excel و PDF لكل شهر (وفرع) محفوظ للرجوع إليه.
export const PayrollArchiveCard = ({ month, branchId }) => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listPayrollArchive().then(setRows).catch(() => setRows([]));
  }, []);

  useEffect(load, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await archivePayroll({ month, lang: i18n.language, ...(branchId ? { branch_id: branchId } : {}) });
      toast.success(t("payrollArchive.saved"));
      load();
    } catch {
      toast.error(t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <NeuCard data-testid="payroll-archive-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="label-xs flex items-center gap-2">
          <Archive className="h-4 w-4 text-primary" /> {t("payrollArchive.title")}
        </p>
        <NeuButton className="px-4 py-2 text-xs" data-testid="payroll-archive-save" disabled={busy} onClick={save}>
          {t("payrollArchive.save")}
        </NeuButton>
      </div>

      {rows.length ? (
        <div className="mt-4 grid gap-2">
          {rows.map((r) => (
            <div key={`${r.month}-${r.branch_id || "all"}-${r.lang}`}
              className="flex flex-wrap items-center gap-3 rounded-2xl p-3 shadow-neu-in"
              data-testid={`payroll-archive-${r.month}-${r.branch_id || "all"}`}>
              <span className="font-mono text-xs font-bold" dir="ltr">{r.month}</span>
              <span className="min-w-0 flex-1 truncate text-xs">{r.branch_name || t("payrollArchive.allBranches")}</span>
              <span className="font-mono text-[11px] text-muted-foreground" dir="ltr">
                {r.period_start} → {r.period_end} · {r.employees} · {r.total_net} {r.currency}
              </span>
              <a className="flex items-center gap-1 text-xs font-bold text-primary" href={fileUrlAuth(r.files?.xlsx?.url)}
                target="_blank" rel="noreferrer" data-testid={`archive-xlsx-${r.month}`}>
                <FileSpreadsheet className="h-4 w-4" /> Excel
              </a>
              <a className="flex items-center gap-1 text-xs font-bold text-primary" href={fileUrlAuth(r.files?.pdf?.url)}
                target="_blank" rel="noreferrer" data-testid={`archive-pdf-${r.month}`}>
                <FileText className="h-4 w-4" /> PDF
              </a>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="payroll-archive-empty">
          {t("payrollArchive.empty")}
        </p>
      )}
    </NeuCard>
  );
};
